import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Inbox as InboxIcon, RefreshCw, Clock, AlertTriangle, CheckCircle, XCircle,
  MinusCircle, AlertCircle, ChevronRight, AtSign, MessageCircle, Calendar,
  Shield, CheckCheck, UserPlus, Undo2, X,
} from 'lucide-react';
import UserAvatar from './UserAvatar';
import './Inbox.css';
import BstId from './BstId';

/*
 * Inbox — the unified activity inbox (WR-005).
 *
 * Reads the WR-004 feed (`/api/inbox/feed`), which merges four sources into
 * one sorted stream: due tasks, @mentions, comment activity, and Design
 * Assurance assignments. Read-state lives on the same Notification documents
 * the bell dropdown uses, so marking something read here clears it there.
 *
 * "My recent work" (completed DAS assignments) stays a separate section: it
 * is history, not something needing attention, and folding it into the feed
 * would dilute the queue.
 *
 * WR-019 — the Inbox is a queue to drain, superseding WR-005's "read rows
 * stay in place". Activity (mentions, comments, subtask assignments) leaves
 * the list the moment it is read: the feed itself returns unread only, so
 * the row is gone on the next load and the animation here is only there to
 * show it going. Due tasks and assurance work are not messages and cannot
 * be dismissed — Clear all steps around them.
 */

// Rows that clear by being read. Everything else in the feed is live state
// and leaves only by being resolved, which is why Clear all cannot touch it.
const CLEARABLE_TYPES = ['mention', 'comment', 'assignment'];
const isClearable = item => CLEARABLE_TYPES.includes(item.type);

// Long enough to read as a movement, short enough not to sit in the way.
const CLEAR_ANIM_MS = 200;
// How long Undo stays on offer.
const UNDO_MS = 6000;

/** Motion is read per call, not once at module load: the OS setting can
 *  change while the tab is open, and a stale value would ignore it. */
function animationMs() {
  const reduced = typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return reduced ? 0 : CLEAR_ANIM_MS;
}

// The WR calls the design-assurance source "DOA"; the app's own UI calls the
// module DAS, so the tab is labelled to match the rest of the product.
const TABS = [
  { id: 'all',      label: 'All',       countKey: null },
  { id: 'mentions', label: 'Mentions',  countKey: 'mentions' },
  { id: 'comments', label: 'Comments',  countKey: 'comments' },
  { id: 'due',      label: 'Due tasks', countKey: 'due' },
  { id: 'doa',      label: 'Assurance', countKey: 'doa' },
];

const GROUPS = [
  { bucket: 'overdue', label: 'Overdue' },
  { bucket: 'today',   label: 'Today' },
  { bucket: 'soon',    label: 'This week' },
  { bucket: 'normal',  label: 'Earlier · Activity' },
];

const EMPTY_COPY = {
  all:      "You're all caught up. Mentions, comments, due tasks and assurance work will land here.",
  mentions: 'No one has mentioned you yet. Type @ in a task, comment or folio to mention someone.',
  comments: 'No comment activity on your tasks.',
  due:      'Nothing due in the next 7 days, and nothing overdue.',
  doa:      'No open Design Assurance assignments.',
};

const ROLE_LABEL = {
  'classifier':              'Classifier',
  'impact-signer-technical': 'Technical impact',
  'impact-signer-structural':'Structural impact',
  'impact-signer-compliance':'Compliance impact',
  'impact-signer-baseline':  'Baseline impact',
  'author':                  'Author',
  'technical-reviewer':      'Technical reviewer',
  'independent-verifier':    'Independent verifier',
  'configuration-updater':   'Configuration update',
  'releaser':                'Release authoriser',
  'closer':                  'Closer',
};

const STATE_SHORT_NAME = {
  1: 'Initiated', 2: 'Classified', 3: 'Impact', 4: 'Drafting', 5: 'Tech rev.',
  6: 'Indep. verif.', 7: 'Config upd.', 8: 'Ready rel.', 9: 'Released',
  10: 'Closed', 11: 'Cancelled',
};

// ── Time helpers ──────────────────────────────────────────────────────

/** Past-facing: how long ago something happened. */
function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/** Future-facing: how long until a deadline, or how far past it. */
function dueLabel(iso) {
  if (!iso) return { label: 'No date', tone: 'none' };
  const ms = new Date(iso).getTime() - Date.now();
  const days = ms / 86400000;
  if (days < 0) {
    const over = Math.max(1, Math.round(-days));
    return { label: over === 1 ? '1 day overdue' : `${over} days overdue`, tone: 'overdue' };
  }
  if (days < 1) return { label: 'Due today',    tone: 'urgent' };
  if (days < 2) return { label: 'Due tomorrow', tone: 'urgent' };
  return { label: `Due in ${Math.ceil(days)} days`, tone: days < 7 ? 'soon' : 'normal' };
}

function outcomeIcon(outcome) {
  switch (outcome) {
    case 'accept':
    case 'accept-with-comments':
    case 'authorise':
      return <CheckCircle size={13} className="ix-outcome-ok" />;
    case 'reject':
    case 'escalate':
    case 'hold':
      return <XCircle size={13} className="ix-outcome-bad" />;
    case 'skipped':
    case 'withdrawn':
      return <MinusCircle size={13} className="ix-outcome-muted" />;
    default:
      return <AlertCircle size={13} className="ix-outcome-muted" />;
  }
}

function typeIcon(type) {
  switch (type) {
    case 'task_due':   return <Calendar size={14} />;
    case 'mention':    return <AtSign size={14} />;
    case 'comment':    return <MessageCircle size={14} />;
    case 'doa':        return <Shield size={14} />;
    // Subtask assignments (WR-011). No tab of their own — they appear in
    // "All" — so the icon is what tells them apart from a mention.
    case 'assignment': return <UserPlus size={14} />;
    default:           return <InboxIcon size={14} />;
  }
}

// Which unread counter a feed row belongs to. Keyed on the feed type, not
// the notification type, since that is what a row carries.
const UNREAD_KEY = {
  comment:    'comments',
  mention:    'mentions',
  assignment: 'assignments',
};

// ── Row ───────────────────────────────────────────────────────────────

/**
 * One feed row. Typed on `item.type` rather than split into four near-
 * identical components — only the icon, the trailing chip and the meta line
 * actually differ.
 */
function InboxRow({ item, onOpen, clearing }) {
  const m = item.meta || {};
  const isDoa = item.type === 'doa';
  const isDue = item.type === 'task_due';
  const deadline = isDue ? item.date : (isDoa ? m.slaTargetAt : null);
  const chip = (isDue || isDoa) ? dueLabel(deadline) : null;
  const unread = item.read === false;

  // The collapse is on the <li>, not the button: the row's own height and
  // its margins have to go, or the list closes up in two steps.
  return (
    <li className={clearing ? 'ix-item is-clearing' : 'ix-item'}>
      <button
        className={`ix-row ix-row-feed${unread ? ' is-unread' : ''}${item.bucket === 'overdue' ? ' is-overdue' : ''}`}
        onClick={() => onOpen(item)}
      >
        <span className={`ix-type ix-type-${item.type}`} aria-hidden="true">
          {typeIcon(item.type)}
        </span>

        {item.actor
          ? <UserAvatar user={item.actor} className="ix-actor" title={item.actor.name} />
          : <span className="ix-actor ix-actor-empty" aria-hidden="true" />}

        <div className="ix-row-main">
          <div className="ix-row-top">
            {/* Task id, where a task is the row's subject (WR-013 §4). DAS
                rows carry their own DT identifier instead. */}
            {item.bstId && <BstId id={item.bstId} />}
            {isDoa && m.identifier && <span className="ix-ident">{m.identifier}</span>}
            {item.project && <span className="ix-project">{item.project.name}</span>}
            {isDoa && m.dtUrgency && m.dtUrgency !== 'routine' && (
              <span className={`ix-urg ix-urg-${m.dtUrgency}`}>
                {m.dtUrgency === 'safety-critical' ? '⚠ Safety' : 'Expedited'}
              </span>
            )}
          </div>

          <div className="ix-row-title">{item.title}</div>

          {item.preview && !isDoa && (
            <div className="ix-row-preview">{item.preview}</div>
          )}

          <div className="ix-row-meta">
            {isDoa ? (
              <>
                <span className="ix-role">{ROLE_LABEL[m.role] || m.role}</span>
                {m.relatedSubject && (
                  <>
                    <span className="ix-sep">·</span>
                    <span className="ix-subject">
                      Subject {m.relatedSubject.number} · {m.relatedSubject.name}
                    </span>
                  </>
                )}
                {m.procedureState && (
                  <>
                    <span className="ix-sep">·</span>
                    <span className="ix-state">
                      State {m.procedureState} — {STATE_SHORT_NAME[m.procedureState]}
                    </span>
                  </>
                )}
              </>
            ) : (
              <>
                {item.actor && <span className="ix-role">{item.actor.name}</span>}
                {item.actor && <span className="ix-sep">·</span>}
                <span>{isDue ? `Due ${new Date(item.date).toLocaleDateString([], { month: 'short', day: 'numeric' })}` : timeAgo(item.date)}</span>
              </>
            )}
          </div>
        </div>

        {chip && (
          <div className={`ix-sla ix-sla-${chip.tone}`}>
            {chip.tone === 'overdue' && <AlertTriangle size={12} />}
            <Clock size={11} />
            {chip.label}
          </div>
        )}

        {unread && <span className="ix-dot" title="Unread" />}
        <ChevronRight size={16} className="ix-chev" />
      </button>
    </li>
  );
}

function SkeletonRows({ count = 4 }) {
  return (
    <ul className="ix-list" aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <li key={i}>
          <div className="ix-row ix-row-skeleton">
            <span className="ix-sk ix-sk-icon" />
            <div className="ix-row-main">
              <span className="ix-sk ix-sk-line ix-sk-short" />
              <span className="ix-sk ix-sk-line" />
            </div>
            <span className="ix-sk ix-sk-chip" />
          </div>
        </li>
      ))}
    </ul>
  );
}

// ── Page ──────────────────────────────────────────────────────────────

function Inbox({ user }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState('all');
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  // Rows on their way out: they are still rendered (so the collapse can be
  // seen) but no longer count as present. WR-019.
  const [clearing, setClearing] = useState(() => new Set());
  // The last Clear all, while Undo is still on offer.
  const [undoBatch, setUndoBatch] = useState(null);

  // Every pending timer, so a tab switch or an unmount cannot leave one
  // firing setState on a gone component.
  const timers = useRef([]);
  const later = useCallback((fn, ms) => {
    const id = setTimeout(fn, ms);
    timers.current.push(id);
    return id;
  }, []);
  useEffect(() => () => { timers.current.forEach(clearTimeout); timers.current = []; }, []);

  // History section, kept separate from the attention queue. Merges
  // completed tasks with completed DAS assignments, so it is useful even
  // for the many users who never touch Design Assurance.
  const [recent, setRecent] = useState([]);

  // Guards against a slow response for tab A landing after the user has
  // already switched to tab B.
  const requestRef = useRef(0);

  const fetchCounts = useCallback(async () => {
    try {
      const res = await axios.get('/inbox/counts');
      setCounts(res.data);
      // The sidebar badge is a separate component polling on its own timer,
      // and clearing the Inbox does not change the route, so nothing would
      // otherwise tell it. WR-019 asks for menu counts to move immediately.
      window.dispatchEvent(new CustomEvent('bastion:inbox-counts', { detail: res.data }));
    } catch {
      // Counts are decoration; a failure here must not blank the feed.
    }
  }, []);

  const fetchFeed = useCallback(async (filter, { append = false, isRefresh = false } = {}) => {
    const reqId = ++requestRef.current;
    try {
      if (append) setLoadingMore(true);
      else if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError('');

      const params = new URLSearchParams({ filter, limit: '25' });
      if (append && cursor) params.set('cursor', cursor);

      const res = await axios.get(`/inbox/feed?${params.toString()}`);
      if (reqId !== requestRef.current) return; // superseded

      setItems(prev => (append ? [...prev, ...(res.data.items || [])] : (res.data.items || [])));
      setCursor(res.data.nextCursor || null);
      setHasMore(!!res.data.hasMore);
    } catch (err) {
      if (reqId !== requestRef.current) return;
      setError(err.response?.data?.error || 'Failed to load your inbox.');
    } finally {
      if (reqId === requestRef.current) {
        setLoading(false);
        setLoadingMore(false);
        setRefreshing(false);
      }
    }
  }, [cursor]);

  const fetchRecent = useCallback(async () => {
    try {
      const res = await axios.get('/inbox/recent-work?days=30');
      setRecent(res.data.items || []);
    } catch {
      setRecent([]);
    }
  }, []);

  useEffect(() => { fetchRecent(); }, [fetchRecent]);

  // Reload whenever the tab changes. Cursor is intentionally excluded from
  // the dep list — including it would refetch page 1 after every Load more.
  useEffect(() => {
    setCursor(null);
    setItems([]);
    setClearing(new Set());
    // The notice names a count in a tab the user has just left; carrying it
    // across would be a claim about the wrong list.
    setUndoBatch(null);
    fetchFeed(tab);
    fetchCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const refreshAll = () => {
    setCursor(null);
    fetchFeed(tab, { isRefresh: true });
    fetchCounts();
    fetchRecent();
  };

  /**
   * Take rows out of the list: mark them clearing (which runs the collapse),
   * then drop them once it has played. With reduced motion the delay is 0,
   * so they are gone on the next paint and nothing animates.
   */
  const removeRows = useCallback((ids) => {
    if (!ids.length) return;
    const set = new Set(ids);
    setClearing(prev => new Set([...prev, ...ids]));
    later(() => {
      setItems(prev => prev.filter(i => !set.has(i.id)));
      setClearing(prev => {
        const next = new Set(prev);
        ids.forEach(id => next.delete(id));
        return next;
      });
    }, animationMs());
  }, [later]);

  /** Move the local counters for one cleared activity row, before the
   *  server round-trip lands. Due/assurance rows have no unread key and
   *  fall through untouched. */
  const decrementCounts = useCallback((type, n = 1) => {
    setCounts(prev => {
      const key = UNREAD_KEY[type];
      if (!prev || !key) return prev;
      const next = { ...prev, unread: { ...prev.unread } };
      const taken = Math.min(n, next.unread[key] || 0);
      next.unread[key] = (next.unread[key] || 0) - taken;
      next.unread.total = Math.max(0, next.unread.total - taken);
      next.badge = Math.max(0, next.badge - taken);
      return next;
    });
  }, []);

  /** Clear all empties whole categories, across pages — so the honest
   *  optimistic move is to zero those counters outright rather than subtract
   *  the number of rows that happened to be loaded. */
  const zeroUnread = useCallback((keys) => {
    setCounts(prev => {
      if (!prev) return prev;
      const unread = { ...prev.unread };
      keys.forEach(k => { unread[k] = 0; });
      const total = (unread.mentions || 0) + (unread.comments || 0) + (unread.assignments || 0);
      const removed = Math.max(0, (prev.unread.total || 0) - total);
      unread.total = total;
      return { ...prev, unread, badge: Math.max(0, prev.badge - removed) };
    });
  }, []);

  const openItem = async (item) => {
    // WR-019: reading an activity row clears it. The row is flipped and
    // collapsed locally first so the list responds instantly; the feed
    // returns unread only, so it is also absent when the user comes back.
    if (item.read === false && item.meta?.notificationId) {
      setItems(prev => prev.map(i => (i.id === item.id ? { ...i, read: true } : i)));
      decrementCounts(item.type);
      removeRows([item.id]);
      try {
        await axios.put(`/notifications/${item.meta.notificationId}/read`);
        // Not awaited by the navigation below — the badge should settle
        // even if the user is already on the task.
        fetchCounts();
      } catch {
        // Non-fatal: the next counts fetch reconciles.
      }
    }
    if (item.targetUrl) navigate(item.targetUrl);
  };

  /**
   * Clear all — empties the clearable items of the active tab. Persistent
   * rows (due tasks, assurance work) are left exactly where they are; the
   * server ignores them too, so this is not a client-side illusion.
   */
  const clearAll = async () => {
    const doomed = items.filter(isClearable).map(i => i.id);
    try {
      const res = await axios.put(`/inbox/read-all?filter=${tab}`);
      const ids = res.data?.cleared || [];
      const updated = res.data?.updated ?? ids.length;

      removeRows(doomed);
      // Which counters this tab's clear emptied. "All" takes every activity
      // type with it, including subtask assignments, which have no tab.
      zeroUnread(tab === 'mentions' ? ['mentions']
        : tab === 'comments' ? ['comments']
        : ['mentions', 'comments', 'assignments']);
      fetchCounts();

      if (updated > 0) {
        const batch = { ids, count: updated, at: Date.now() };
        setUndoBatch(batch);
        later(() => setUndoBatch(cur => (cur && cur.at === batch.at ? null : cur)), UNDO_MS);
      }

      // The clear reached beyond the loaded page whenever there was more to
      // load, so the local list is no longer the whole truth. Re-read page 1
      // rather than guessing what is left.
      if (hasMore) {
        later(() => { setCursor(null); fetchFeed(tab, { isRefresh: true }); }, animationMs() + 20);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to clear these items.');
    }
  };

  /** Undo the last Clear all: the same documents back to unread, which is
   *  what puts them back in the feed. */
  const undoClear = async () => {
    if (!undoBatch) return;
    const { ids } = undoBatch;
    setUndoBatch(null);
    try {
      await axios.put('/inbox/unread', { ids });
      setCursor(null);
      fetchFeed(tab, { isRefresh: true });
      fetchCounts();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to restore those items.');
    }
  };

  const tabCount = (t) => {
    if (!counts || !t.countKey) return 0;
    if (t.countKey === 'mentions' || t.countKey === 'comments') return counts.unread?.[t.countKey] || 0;
    return counts.actionable?.[t.countKey] || 0;
  };

  // Something to clear in this tab? Persistent rows never qualify, so the
  // button is absent on Due tasks and Assurance rather than being a no-op.
  const clearableHere = items.some(i => isClearable(i) && !clearing.has(i.id));
  // "All" mixes both kinds, so the label has to say what will actually go.
  const clearLabel = tab === 'all' ? 'Clear all activity' : 'Clear all';

  // In "All" the feed is grouped by attention bucket; the API already sorts
  // so each bucket is contiguous. Other tabs render a flat list.
  const grouped = tab === 'all'
    ? GROUPS.map(g => ({ ...g, rows: items.filter(i => i.bucket === g.bucket) })).filter(g => g.rows.length)
    : null;

  return (
    <div className="ix">
      <header className="ix-header">
        <div className="ix-title-wrap">
          <div className="ix-eyebrow"><InboxIcon size={13} /> Personal queue</div>
          <h1>Inbox</h1>
          <p className="ix-summary">
            {counts && counts.badge > 0
              ? <>You have <strong>{counts.badge}</strong> {counts.badge === 1 ? 'item' : 'items'} needing attention.</>
              : 'Nothing waiting on you right now.'}
          </p>
        </div>
        <button className="ix-refresh" onClick={refreshAll} disabled={refreshing} title="Refresh">
          <RefreshCw size={14} className={refreshing ? 'is-spinning' : ''} />
          Refresh
        </button>
      </header>

      <div className="ix-tabbar" role="tablist">
        <div className="ix-tabs">
          {TABS.map(t => {
            const n = tabCount(t);
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={tab === t.id}
                className={`ix-tab${tab === t.id ? ' is-active' : ''}`}
                onClick={() => setTab(t.id)}
              >
                {t.label}
                {n > 0 && <span className="ix-tab-count">{n > 99 ? '99+' : n}</span>}
              </button>
            );
          })}
        </div>
        {(tab === 'all' || tab === 'mentions' || tab === 'comments') && clearableHere && (
          <button
            className="ix-markall"
            onClick={clearAll}
            title={tab === 'all'
              ? 'Clears mentions, comments and subtask assignments. Due tasks and assurance work stay.'
              : 'Clears every item in this tab.'}
          >
            <CheckCheck size={14} /> {clearLabel}
          </button>
        )}
      </div>

      {/* Undo — the one affordance that makes Clear all safe to press. */}
      {undoBatch && (
        <div className="ix-notice" role="status">
          <span>
            Cleared {undoBatch.count} {undoBatch.count === 1 ? 'item' : 'items'}
            {tab === 'all' && ' — due tasks and assurance work were left in place'}
          </span>
          <div className="ix-notice-actions">
            <button className="ix-notice-undo" onClick={undoClear}>
              <Undo2 size={13} /> Undo
            </button>
            <button className="ix-notice-close" onClick={() => setUndoBatch(null)} aria-label="Dismiss">
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="ix-error" role="alert">
          <AlertCircle size={16} />
          <span>{error}</span>
          <button className="ix-retry" onClick={() => fetchFeed(tab)}>Retry</button>
        </div>
      )}

      {loading ? (
        <SkeletonRows />
      ) : items.length === 0 && !error ? (
        <div className="ix-empty">
          <CheckCircle size={28} strokeWidth={1.5} />
          <p>{EMPTY_COPY[tab]}</p>
        </div>
      ) : (
        <>
          {grouped
            ? grouped.map(g => (
                <section className="ix-group" key={g.bucket}>
                  <h2 className={`ix-group-title ix-group-${g.bucket}`}>
                    {g.label}<span className="ix-group-count">{g.rows.length}</span>
                  </h2>
                  <ul className="ix-list">
                    {g.rows.map(item => (
                      <InboxRow key={item.id} item={item} onOpen={openItem} clearing={clearing.has(item.id)} />
                    ))}
                  </ul>
                </section>
              ))
            : (
              <ul className="ix-list">
                {items.map(item => (
                  <InboxRow key={item.id} item={item} onOpen={openItem} clearing={clearing.has(item.id)} />
                ))}
              </ul>
            )}

          {hasMore && (
            <button className="ix-loadmore" onClick={() => fetchFeed(tab, { append: true })} disabled={loadingMore}>
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          )}
        </>
      )}

      {/* ── My recent work ───────────────────────────────────────── */}
      <section className="ix-section ix-recent-section">
        <h2 className="ix-section-title">
          My recent work
          {recent.length > 0 && <span className="ix-section-count">last 30d</span>}
        </h2>
        {recent.length === 0 ? (
          <div className="ix-empty ix-empty-small">
            <p>Nothing completed in the last 30 days. Tasks you finish will show up here.</p>
          </div>
        ) : (
          <ul className="ix-list ix-list-recent">
            {recent.map(item => {
              const m = item.meta || {};
              const isDoa = item.type === 'doa_done';
              return (
                <li key={item.id}>
                  <button
                    className="ix-row ix-row-recent"
                    onClick={() => item.targetUrl && navigate(item.targetUrl)}
                  >
                    <div className="ix-row-main">
                      <div className="ix-row-top">
                        {isDoa
                          ? outcomeIcon(m.outcome)
                          : <CheckCircle size={13} className="ix-outcome-ok" />}
                        {isDoa && m.identifier && <span className="ix-ident">{m.identifier}</span>}
                        {item.project && <span className="ix-project">{item.project.name}</span>}
                      </div>
                      <div className="ix-row-title ix-row-title-recent">{item.title}</div>
                      <div className="ix-row-meta">
                        {isDoa ? (
                          <>
                            <span className="ix-role">{ROLE_LABEL[m.role] || m.role}</span>
                            <span className="ix-sep">·</span>
                            <span className="ix-outcome">{m.outcome}</span>
                          </>
                        ) : (
                          <span className="ix-role">Task completed</span>
                        )}
                      </div>
                    </div>
                    <div className="ix-sla ix-sla-none">
                      <Clock size={11} />
                      {/* Tasks finished before completion times were recorded
                          fall back to their last-edit time — say so rather
                          than presenting a guess as fact. */}
                      {item.approximate ? `~${timeAgo(item.date)}` : timeAgo(item.date)}
                    </div>
                    <ChevronRight size={16} className="ix-chev" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

    </div>
  );
}

export default Inbox;
